import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Plane, MapPin } from "lucide-react";

export default function PlaceAutocomplete({ label, placeholder, value, onSelect, testId }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const skipFetch = useRef(false);

  useEffect(() => {
    if (skipFetch.current) { skipFetch.current = false; return; }
    if (input.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/planning/places", { params: { query: input.trim() } });
        setSuggestions(data.data || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (p) => {
    skipFetch.current = true;
    setInput(p.iata_code ? `${p.name} (${p.iata_code})` : p.name);
    setSuggestions([]);
    setOpen(false);
    onSelect(p);
  };

  return (
    <div className="relative" ref={boxRef}>
      <label className="label-tag block mb-2">{label}</label>
      <input
        className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember transition-colors"
        value={input}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => { setInput(e.target.value); if (value) onSelect(null); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
      />
      {loading && (
        <span className="absolute right-3 top-9 text-xs text-sand-400 animate-pulse">…</span>
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 w-full bg-surface border border-sand-300 mt-1 max-h-72 overflow-y-auto shadow-lg" data-testid={`${testId}-dropdown`}>
          {suggestions.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-sand-50 border-b border-sand-100 last:border-0 flex items-start gap-2.5"
              data-testid={`${testId}-option-${p.iata_code || p.id}`}
            >
              {p.type === "airport"
                ? <Plane size={14} className="mt-0.5 text-sand-400 shrink-0" />
                : <MapPin size={14} className="mt-0.5 text-sand-400 shrink-0" />}
              <span>
                <span className="block text-sm font-medium text-sand-950">
                  {p.name} {p.iata_code ? <span className="font-mono text-sand-500">({p.iata_code})</span> : null}
                </span>
                <span className="block text-xs text-sand-500">
                  {[p.type === "airport" ? p.city_name : null, p.country_code].filter(Boolean).join(", ") || p.type}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
